import { useState } from 'react';
import supabase from '../utils/supabase';

interface LoginCredentials {
  email: string;
  password: string;
}

interface UseLoginResult {
  login: (credentials: LoginCredentials) => Promise<{ success: boolean; token?: string; error?: string }>;
  forgotPassword: (email: string) => Promise<{ success: boolean; message?: string; error?: string }>;
  resetPassword: (token: string, password: string) => Promise<{ success: boolean; message?: string; error?: string }>;
  isLoading: boolean;
  error: string | null;
}

export const useLogin = (): UseLoginResult => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const login = async ({ email, password }: LoginCredentials) => {
    setIsLoading(true);
    setError(null);

    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      });

      if (error) {
        const errorMessage = error.message || 'Login failed. Please try again.';
        setError(errorMessage);
        return { success: false, error: errorMessage };
      }

      const token = data?.session?.access_token;
      if (token) {
        sessionStorage.setItem('token', token);
        return { success: true, token };
      }

      const errorMessage = 'Login did not return a session token.';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } catch (err: any) {
      const errorMessage = err?.message || 'Login failed. Please try again.';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setIsLoading(false);
    }
  };

  const forgotPassword = async (email: string) => {
    setIsLoading(true);
    setError(null);

    try {
      // Supabase will send a password reset email containing a link.
      const { data, error } = await supabase.auth.resetPasswordForEmail(email);
      if (error) {
        const errorMessage = error.message || 'Failed to send reset email.';
        setError(errorMessage);
        return { success: false, error: errorMessage };
      }

      return { success: true, message: 'If that email exists, a reset link has been sent.' };
    } catch (err: any) {
      const errorMessage = err?.message || 'Failed to send reset email.';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setIsLoading(false);
    }
  };

  const resetPassword = async (token: string, password: string) => {
    setIsLoading(true);
    setError(null);

    try {
      // Set a temporary session using the token so we can update the user's password.
      // Supabase's reset flow typically redirects the user with an access token.
      await supabase.auth.setSession({ access_token: token, refresh_token: '' });

      const { data, error } = await supabase.auth.updateUser({ password });
      if (error) {
        const errorMessage = error.message || 'Failed to reset password.';
        setError(errorMessage);
        return { success: false, error: errorMessage };
      }

      // Clear session for safety.
      try {
        await supabase.auth.signOut();
      } catch {
        // ignore
      }

      return { success: true, message: 'Password reset successful' };
    } catch (err: any) {
      const errorMessage = err?.message || 'Failed to reset password.';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setIsLoading(false);
    }
  };

  return {
    login,
    forgotPassword,
    resetPassword,
    isLoading,
    error
  };
};